import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CartContext } from '../../App';
import { useAuthStore } from '../../store/auth';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import apiInstance from '../../utils/axios';
import toast from '../../components/ui/Toast';
import SEO from '../../components/SEO';
import GetCurrentAddress from '../plugin/UserCountry';
import getCartID from '../plugin/cartID';
import UserData from '../plugin/UserData';
import { addToWishlist } from '../plugin/addToWishlist';
import QuantityInputBasic from '@/components/commerce-ui/quantity-input-basic';
import PriceFormat_Basic from '@/components/commerce-ui/price-format-basic';
import VariantSelectorImages from '@/components/ui/VariantSelectorImages';
import { ArrowLeft, ShoppingCart, Heart, Truck, Frown, Search as SearchIcon } from 'lucide-react';
import './ProductDetail.css';


function ProductDetail() {
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [specifications, setSpecifications] = useState([]);
    const [gallery, setGallery] = useState([]);
    const [color, setColor] = useState([]);
    const [size, setSize] = useState([]);
    const [quantity, setQuantity] = useState(1);
    const [selectedSize, setSelectedSize] = useState(''); // Don't call t() in initial state
    const [selectedColor, setSelectedColor] = useState(''); // Don't call t() in initial state
    const [colorImage, setColorImage] = useState("");
    const [colorGallery, setColorGallery] = useState([]);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [translatedRelatedProducts, setTranslatedRelatedProducts] = useState([]);
    const [currentImage, setCurrentImage] = useState("");
    const [isZoomed, setIsZoomed] = useState(false);
    const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
    const [displayedGallery, setDisplayedGallery] = useState([]);
    const [currentPrice, setCurrentPrice] = useState(0);
    const [priceLoading, setPriceLoading] = useState(false);
    

    const [cartCount, updateCartCountAndNotify] = useContext(CartContext);
    const { t } = useLanguage();
    // Currency context available if needed


    const param = useParams();
    const currentAddress = GetCurrentAddress();
    const userData = UserData();
    let cart_id = getCartID();
    const slug = param.slug;
    const urlColor = param.color;
    const urlSize = param.size;
    const isLoggedIn = useAuthStore((state) => state.isLoggedIn);

    // Toast system is imported from Toast component

    // Dynamic label for size/capacity on frontend
    const sizeLabel = product?.size_label || t('size');

    // Helper function to create gallery like Amazon - main product image + color-specific images
    const createUniqueGallery = (mainProductImage, colorImages, productGalleryImages) => {
        const uniqueImages = [];
        const seenImages = new Set();
        
        // 1. Always add main product image first (the product itself)
        if (mainProductImage) {
            uniqueImages.push({ image: mainProductImage, isMain: true, type: 'product' });
            seenImages.add(mainProductImage);
        }
        
        // 2. Add color-specific images (different from main product image)
        if (colorImages && Array.isArray(colorImages)) {
            colorImages.forEach(img => {
                const imageUrl = typeof img === 'string' ? img : img.image;
                if (imageUrl && !seenImages.has(imageUrl)) {
                    uniqueImages.push({ image: imageUrl, isMain: false, type: 'color' });
                    seenImages.add(imageUrl);
                }
            });
        }
        
        // 3. Add additional product gallery images (if any)
        if (productGalleryImages && Array.isArray(productGalleryImages)) {
            productGalleryImages.forEach(img => {
                const imageUrl = typeof img === 'string' ? img : img.image;
                if (imageUrl && !seenImages.has(imageUrl)) {
                    uniqueImages.push({ image: imageUrl, isMain: false, type: 'gallery' });
                    seenImages.add(imageUrl);
                }
            });
        }
        
        return uniqueImages;
    };

    // Function to get current price based on selected variants
    const getCurrentPrice = () => {
        if (!product?.variants || product.variants.length === 0) {
            return product?.price || 0;
        }

        // Find matching variant
        const variant = product.variants.find(v => {
            const colorMatch = (!selectedColor || selectedColor === 'No Color') 
                ? (!v.color_name || v.color_name === 'No Color')
                : v.color_name === selectedColor;
            
            const sizeMatch = (!selectedSize || selectedSize === 'No Size')
                ? (!v.size_name || v.size_name === 'No Size') 
                : v.size_name === selectedSize;

            return colorMatch && sizeMatch;
        });

        return variant?.price || product?.price || 0;
    };

    

    // Function to check if a color-size combination is available
    const isVariantAvailable = (colorName, sizeName) => {
        if (!product?.variants || product.variants.length === 0) {
            return true; // If no variants, assume available
        }

        // Handle undefined values
        if (colorName === undefined || sizeName === undefined) {
            return true; // If values are undefined, assume available
        }

        const variant = product.variants.find(v => {
            const colorMatch = (!colorName || colorName === 'No Color') 
                ? (!v.color_name || v.color_name === 'No Color')
                : v.color_name === colorName;
            
            const sizeMatch = (!sizeName || sizeName === 'No Size')
                ? (!v.size_name || v.size_name === 'No Size') 
                : v.size_name === sizeName;

            return colorMatch && sizeMatch;
        });

        return variant && variant.stock_qty > 0;
    };

    // Function to check if a color has any available sizes
    const isColorAvailable = (colorName) => {
        if (!product?.variants || product.variants.length === 0) {
            return true;
        }

        // Check if this color has any variants with stock
        return product.variants.some(v => {
            const colorMatch = (!colorName || colorName === 'No Color') 
                ? (!v.color_name || v.color_name === 'No Color')
                : v.color_name === colorName;
            
            return colorMatch && v.stock_qty > 0;
        });
    };

    // Function to check if a size has any available colors
    const isSizeAvailable = (sizeName) => {
        if (!product?.variants || product.variants.length === 0) {
            return true;
        }

        // Check if this size has any variants with stock
        return product.variants.some(v => {
            const sizeMatch = (!sizeName || sizeName === 'No Size')
                ? (!v.size_name || v.size_name === 'No Size') 
                : v.size_name === sizeName;
            
            return sizeMatch && v.stock_qty > 0;
        });
    };

    // Function to check if current selection is available
    const isCurrentSelectionAvailable = () => {
        return isVariantAvailable(selectedColor, selectedSize);
    };

    // Memoized current price calculation for better performance
    const currentPriceValue = React.useMemo(() => {
        return getCurrentPrice();
    }, [selectedColor, selectedSize, product]);

    // Update current price when variants change
    useEffect(() => {
        if (product) {
            setPriceLoading(true);
            // Simulate loading delay for metallic effect
            const timer = setTimeout(() => {
                setCurrentPrice(currentPriceValue);
                setPriceLoading(false);
            }, 300);
            
            return () => clearTimeout(timer);
        }
    }, [currentPriceValue, product]);

    // Initialize price when product loads
    useEffect(() => {
        if (product) {
            setCurrentPrice(currentPriceValue);
        }
    }, [product, currentPriceValue]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]); // Scroll to top when product changes

    useEffect(() => {
        const fetchProductData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // Reset all product-specific state to prevent showing old data
                setProduct(null);
                setSpecifications([]);
                setGallery([]);
                setColor([]);
                setSize([]);
                setSelectedSize('');
                setSelectedColor('');
                setColorImage("");
                setColorGallery([]);
                setRelatedProducts([]);
                setTranslatedRelatedProducts([]);
                setCurrentImage("");
                setDisplayedGallery([]);
                setCurrentPrice(0);
                
                // Build API URL based on whether color and size are provided
                let apiUrl = `/products/${slug}`;
                if (urlColor && urlSize) {
                    apiUrl = `/products/${slug}/${encodeURIComponent(urlColor)}/${encodeURIComponent(urlSize)}`;
                }
                
                const res = await apiInstance.get(apiUrl);
                
                if (!res.data) {
                    throw new Error('Product not found');
                }
                
                // Store original product data
                setProduct(res.data);
                
                setSpecifications(res.data.specification || []);
                setGallery(res.data.gallery || []);
                
                setColor(res.data.color || []);
                setSize(res.data.size || []);
                setCurrentImage(res.data.image);
                
                // Initialize displayed gallery with only the first color's images
                if (res.data.color?.length > 0) {
                    const firstColor = res.data.color[0];
                    setDisplayedGallery(createUniqueGallery(firstColor.image, firstColor.galleries || [], []));
                } else {
                setDisplayedGallery(createUniqueGallery(res.data.image, [], res.data.gallery));
                }
                
                // Handle size selection from URL or default
                if (res.data.size?.length > 0) {
                    if (urlSize && res.data.size.some(s => s.name === urlSize)) {
                        setSelectedSize(urlSize);
                    } else {
                        setSelectedSize(res.data.size[0].name);
                    }
                } else {
                    setSelectedSize('No Size');
                }
                
                // Handle color selection from URL or default
                if (res.data.color?.length > 0) {
                    if (urlColor && res.data.color.some(c => c.name === urlColor)) {
                        setSelectedColor(urlColor);
                        const selectedColorObj = res.data.color.find(c => c.name === urlColor);
                        setColorImage(selectedColorObj.image);
                        setColorGallery(selectedColorObj.galleries || []);
                    } else {
                        // Always select the first color by default
                        const firstColor = res.data.color[0];
                        setSelectedColor(firstColor.name);
                        setColorImage(firstColor.image);
                        setColorGallery(firstColor.galleries || []);
                        setCurrentImage(firstColor.image); // Set as main image too
                    }
                } else {
                    setSelectedColor('No Color');
                }
                
                setLoading(false);
            } catch (err) {
                console.error('Error fetching product:', err);
                setError(err.response?.data?.detail || err.message || 'Producto no encontrado');
                setProduct(null);
                setLoading(false);
            }
        };

        fetchProductData();
    }, [slug]); // Removed translation dependencies

    // Server-provided translations are applied from API response

    const navigate = useNavigate();
    
    // Function to update URL with color and size parameters (without page refresh)
    const updateProductURL = (colorName, sizeName) => {
        const encodedColor = encodeURIComponent(colorName);
        const encodedSize = encodeURIComponent(sizeName);
        const newPath = `/detail/${slug}/${encodedColor}/${encodedSize}/`;
        
        // Update URL without page refresh using browser history API
        // Use replaceState instead of pushState to avoid creating history entries
        window.history.replaceState({}, '', newPath);
    };

    // Memoized color button click handler for better performance
    const handleColorButtonClick = React.useCallback((product_id, colorName, colorImage, colorGalleries) => {
        setSelectedColor(colorName);
        setColorImage(colorImage);
        setColorGallery(colorGalleries);
        setCurrentImage(colorImage);
        setIsZoomed(false);
        
        // Update displayed gallery when color changes - only color images for selected color
        setDisplayedGallery(createUniqueGallery(colorImage, colorGalleries, []));
        
        // Update URL with new color and size
        updateProductURL(colorName, selectedSize);
    }, [selectedSize, updateProductURL]);

    // Memoized size button click handler — toggle deselect on re-click
    const handleSizeButtonClick = React.useCallback((sizeName) => {
        if (selectedSize === sizeName) {
            // Deselect: clicking same size again unselects it
            setSelectedSize('No Size');
            updateProductURL(selectedColor, 'No Size');
        } else {
            setSelectedSize(sizeName);
            updateProductURL(selectedColor, sizeName);
        }
    }, [selectedSize, selectedColor, updateProductURL]);

    // Memoized thumbnail click handler for better performance
    const handleThumbnailClick = React.useCallback((image, index) => {
        // Find the clicked image in the displayed gallery
        const clickedImage = displayedGallery.find(img => img.image === image);
        
        // If the clicked image is not already the current image
        if (currentImage !== image) {
            // If the clicked image was the main product image (isMain: true)
            if (clickedImage?.isMain) {
                // Set the current image to the main product image
                setCurrentImage(image);
            } else {
                // For other gallery images, set them as current image
                setCurrentImage(image);
            }
        }
    }, [displayedGallery, currentImage]);


    // Memoized mouse event handlers for better performance
    const handleMouseMove = React.useCallback((e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        // Clamp values between 0 and 100
        setZoomPosition({ 
            x: Math.max(0, Math.min(100, x)), 
            y: Math.max(0, Math.min(100, y)) 
        });
    }, []);

    const handleMouseEnter = React.useCallback(() => {
        setIsZoomed(true);
    }, []);

    const handleMouseLeave = React.useCallback(() => {
        setIsZoomed(false);
    }, []);

    // Memoized add to cart handler for better performance
    const handleAddToCart = React.useCallback(async () => {
        if (!product) return;

        // Check product stock
        if (product.stock_qty <= 0) {
            toast.error(t('productOutOfStock'));
            return;
        }

        if (quantity > product.stock_qty) {
            toast.error(t('onlyXAvailable').replace('{qty}', product.stock_qty));
            return;
        }

        if (quantity > product.max_cart_limit) {
            toast.error(t('maxUnitsPerCart').replace('{qty}', product.max_cart_limit));
            return;
        }

        // Validate color selection if product has colors
        if (color.length > 0) {
            if (selectedColor === 'No Color') {
                toast.warning(t('pleaseSelectColor'));
                return;
            }
            const selectedColorObj = color.find(c => c.name === selectedColor);
            if (selectedColorObj) {
                if (selectedColorObj.stock_qty <= 0) {
                    toast.error(t('colorOutOfStock'));
                    return;
                }
                if (quantity > selectedColorObj.stock_qty) {
                    toast.error(t('onlyXInStockForColor').replace('{qty}', selectedColorObj.stock_qty).replace('{item}', selectedColor));
                    return;
                }
            }
        }

        // Validate size selection if product has sizes
        if (size.length > 0) {
            if (selectedSize === 'No Size') {
                toast.warning(t('pleaseSelectSize').replace('{label}', sizeLabel.toLowerCase()));
                return;
            }
            const selectedSizeObj = size.find(s => s.name === selectedSize);
            if (selectedSizeObj) {
                if (selectedSizeObj.stock_qty <= 0) {
                    toast.error(t('sizeOutOfStock').replace('{label}', sizeLabel));
                    return;
                }
                if (quantity > selectedSizeObj.stock_qty) {
                    toast.error(t('onlyXInStockForSize').replace('{qty}', selectedSizeObj.stock_qty).replace('{item}', selectedSize));
                    return;
                }
            }
        }

        // Validate variant combination
        if (product?.variants && product.variants.length > 0) {
            if (!isVariantAvailable(selectedColor, selectedSize)) {
                toast.warning(t('combinationNotAvailable'));
                return;
            }
        }

        try {
            const formdata = new FormData();
            formdata.append('product_id', product.id);
            formdata.append('user_id', userData?.user_id || 0);
            formdata.append('qty', quantity);
            formdata.append('price', currentPrice || product.price);
            formdata.append('shipping_ammount', product.shipping_ammount);
            formdata.append('country', currentAddress.country);
            formdata.append('size', selectedSize);
            formdata.append('color', selectedColor);
            formdata.append('cart_id', cart_id);

            await apiInstance.post('cart-view/', formdata);

            const url = userData ? `cart-list/${cart_id}/${userData?.user_id}/` : `cart-list/${cart_id}/`;
            const res = await apiInstance.get(url);
            const cartItemsCount = res.data.cart_items?.length || 0;
            updateCartCountAndNotify(cartItemsCount);

            toast.cart(t('addedToCart'));
        } catch (error) {
            let errorMessage = t('errorAddingToCart');
            if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            }
            toast.error(errorMessage);
        }
    }, [product, quantity, color, selectedColor, size, selectedSize, currentPrice, userData, cart_id, currentAddress, updateCartCountAndNotify, t, sizeLabel]);

    // Handler used by the new ProductVariant_01 component
    const handleAddToCartFromVariant = React.useCallback(async (payload) => {
        if (!product) return;

        // payload: { variantId, quantity, price, salePrice, totalPrice, variantLabel }
        const variantId = payload.variantId;
        const qty = payload.quantity || 1;

        // Find matching variant by sku/value or id
        const variant = (product.variants || []).find(v => {
            const vid = v.sku ?? v.value ?? v.id ?? `${v.id}`;
            return `${vid}` === `${variantId}` || `${v.id}` === `${variantId}`;
        }) || null;

        // Basic validations (mirror existing logic)
        const availableQty = variant?.stock_qty ?? variant?.availableQuantity ?? product.stock_qty ?? 0;

        if (availableQty <= 0) {
            toast.error(t('variantOutOfStock'));
            return;
        }

        if (qty > availableQty) {
            toast.error(t('onlyXAvailable').replace('{qty}', availableQty));
            return;
        }

        if (qty > product.max_cart_limit) {
            toast.error(t('maxUnitsPerCart').replace('{qty}', product.max_cart_limit));
            return;
        }

        try {
            const formdata = new FormData();
            formdata.append('product_id', product.id);
            formdata.append('user_id', userData?.user_id || 0);
            formdata.append('qty', qty);
            formdata.append('price', payload.price ?? payload.salePrice ?? currentPrice ?? product.price);
            formdata.append('shipping_ammount', product.shipping_ammount);
            formdata.append('country', currentAddress.country);
            // include size/color if available on the variant
            formdata.append('size', variant?.size_name ?? '');
            formdata.append('color', variant?.color_name ?? '');
            formdata.append('cart_id', cart_id);

            await apiInstance.post('cart-view/', formdata);

            const url = userData ? `cart-list/${cart_id}/${userData?.user_id}/` : `cart-list/${cart_id}/`;
            const res = await apiInstance.get(url);
            const cartItemsCount = res.data.cart_items?.length || 0;
            updateCartCountAndNotify(cartItemsCount);

            toast.cart(t('addedToCart'));
            setTimeout(() => { window.location.href = '/'; }, 1500);
        } catch (error) {
            let errorMessage = t('errorAddingToCart');
            if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            }
            toast.error(errorMessage);
        }
    }, [product, currentPrice, userData, cart_id, currentAddress, updateCartCountAndNotify]);


    useEffect(() => {
        if (product?.category?.id) {
            apiInstance.get(`products/?category=${product.category.id}`).then((res) => {
                // Handle both direct array and paginated response
                const productsData = Array.isArray(res.data) ? res.data : (res.data.results || []);
                const filteredProducts = productsData.filter((p) => p.id !== product.id);
                setRelatedProducts(filteredProducts);
                
                // No translation needed - just use the products directly
                setTranslatedRelatedProducts(filteredProducts);
            });
        }
    }, [product]);


    if (loading) {
        return (
            <div className="product-page">
                <div className="loading-wrapper">
                    <div className="loading-spinner"></div>
                                    <p className="loading-text">{t('loading')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="product-page">
                <div className="error-wrapper">
                    <Frown className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="error-text">{error}</p>
                    <Link to="/" className="error-btn">{t('back')}</Link>
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="product-page">
                <div className="error-wrapper">
                    <SearchIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="error-text">{t('noProductsFound') || 'Producto no encontrado'}</p>
                    <Link to="/" className="error-btn">{t('back')}</Link>
                </div>
            </div>
        );
    }

    const discountPercent = product.old_price > 0 
        ? Math.round(((product.old_price - (currentPrice || product.price)) / product.old_price) * 100) 
        : 0;

    return (
        <div className="product-page">
            <SEO 
                title={`${product?.title_translated || product?.title} - Comprar en ASMAR SHOP`}
                description={`${product?.description_translated || product?.description?.substring(0, 155)} - Compra en ASMAR SHOP Paraguay. Envíos a todo el país. Productos originales con los mejores precios.`}
                keywords={`${product?.title}, ${product?.category?.title || ''}, comprar ${product?.title}, ${product?.title} paraguay, venta ${product?.title}, ${product?.category?.title || ''} paraguay, asmar shop`}
                image={product?.image}
                url={`https://asmarshop.com/detail/${product?.slug}`}
                type="product"
                product={{
                    title: product?.title_translated || product?.title,
                    description: product?.description_translated || product?.description,
                    image: product?.image,
                    price: product?.price,
                    stock_qty: product?.stock_qty,
                    brand: product?.brand,
                    rating: product?.rating,
                    review_count: product?.review_count,
                    sku: product?.sku || product?.slug,
                    category: product?.category?.title
                }}
                breadcrumbs={[
                    { name: 'Inicio', url: 'https://asmarshop.com' },
                    ...(product?.category ? [{ name: product.category.title, url: `https://asmarshop.com/products?category=${product.category.slug}` }] : []),
                    { name: product?.title_translated || product?.title, url: `https://asmarshop.com/detail/${product?.slug}` }
                ]}
                includeOrganizationSchema={false}
                includeProductSchema={true}
            />

            <div className="product-detail-container">
                {/* Back Button */}
                <button onClick={() => window.history.back()} className="back-button">
                    <ArrowLeft className="h-4 w-4" />
                    {t('back')}
                </button>

                {/* Main Product Layout */}
                <div className="product-layout">
                    {/* Gallery Section */}
                    <div className="gallery-section">
                        <div 
                            className={`main-image-wrapper ${isZoomed ? 'zoomed' : ''}`}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                            onMouseMove={handleMouseMove}
                            style={{
                                '--zoom-x': `${zoomPosition.x}%`,
                                '--zoom-y': `${zoomPosition.y}%`
                            }}
                        >
                            {discountPercent > 0 && (
                                <span className="image-discount-badge">-{discountPercent}%</span>
                            )}
                            <img
                                src={currentImage}
                                className="main-image"
                                alt={product?.title}
                            />
                            </div>
                        <div className="thumbnails-row">
                            {displayedGallery.slice(0, 6).map((img, index) => (
                                <div
                                    key={index}
                                    className={`thumb ${currentImage === img.image ? 'active' : ''}`}
                                    onClick={() => handleThumbnailClick(img.image, index)}
                                >
                                    <img src={img.image} alt={`${t('viewImage')} ${index + 1}`} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Product Info Section */}
                    <div className="product-info-section">
                        {/* Category */}
                        {product?.category?.title && (
                            <span className="category-badge">{product.category.title}</span>
                        )}

                        {/* Title */}
                        <h1 className="product-title">{product?.title}</h1>

                        {/* Price Block */}
                        <div className="price-block">
                            <div className="price-row">
                                <span className="current-price">
                                    <PriceFormat_Basic value={currentPrice || product.price} />
                                </span>
                                {product.old_price > 0 && (
                                    <span className="original-price">
                                        <PriceFormat_Basic value={product.old_price} />
                                    </span>
                                )}
                                {discountPercent > 0 && (
                                    <span className="discount-tag">-{discountPercent}% OFF</span>
                                )}
                            </div>

                        </div>

                        {/* Shipping */}
                        <div className={`shipping-info ${product.shipping_ammount === 0 || product.shipping_ammount === '0.00' || product.shipping_ammount === '0' ? 'free' : ''}`}>
                            <Truck className="h-5 w-5 shipping-icon" />
                            <span className="shipping-text">
                                {product.shipping_ammount === 0 || product.shipping_ammount === '0.00' || product.shipping_ammount === '0' ? (t('decidedInWhatsApp') || 'Será decidido no WhatsApp') : `${t('shipping')}: $${product.shipping_ammount}`}
                            </span>
                        </div>

                        {/* Stock Status - Only show for low stock or out of stock */}
                        {(product.stock_qty <= 5) && (
                            <div className={`stock-status ${product.stock_qty > 0 ? 'low-stock' : 'out-of-stock'}`}>
                                <span className="stock-dot"></span>
                                <span>
                                    {product.stock_qty > 0 
                                        ? `${t('onlyLeft') || '¡Solo quedan'} ${product.stock_qty}!` 
                                        : t('outOfStock')}
                                </span>
                            </div>
                        )}

                        {/* Variants Section */}
                        <div className="variants-section">
                            {/* Size Selection */}
                            {size.length > 0 && (
                                <div className="variant-group">
                                    <label className="variant-label">
                                        {product?.size_label || t('size')}
                                        {selectedSize && selectedSize !== 'No Size' && (
                                            <span className="variant-selected-value">: {selectedSize}</span>
                                        )}
                                    </label>
                                    <div className="variant-buttons">
                                        {size.map((s, index) => {
                                            const hasAnyStock = isSizeAvailable(s.name);
                                            const isComboAvailable = isVariantAvailable(selectedColor, s.name);
                                            const isSelected = selectedSize === s.name;
                                            const isDisabled = !hasAnyStock || !isComboAvailable;

                                            return (
                                                <button
                                                    key={index}
                                                    className={`variant-btn ${isSelected ? 'selected' : ''} ${isDisabled ? 'unavailable' : ''}`}
                                                    onClick={() => !isDisabled && handleSizeButtonClick(s.name)}
                                                    disabled={isDisabled}
                                                    title={isSelected ? t('clickToDeselect') : s.name}
                                                >
                                                    {isSelected && <span className="variant-check">✓</span>}
                                                    {s.name}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Color Selection */}
                            {color.length > 0 && (
                                <div className="variant-group">
                                    <label className="variant-label">
                                        {t('color')}{selectedColor && selectedColor !== 'No Color' && (
                                            <span className="variant-selected-value">: {selectedColor}</span>
                                        )}
                                    </label>
                                    <VariantSelectorImages
                                        value={selectedColor}
                                        onValueChange={(colorName) => {
                                            const selectedColorData = color.find(c => c.name === colorName);
                                            if (selectedColorData) {
                                                handleColorButtonClick(product.id, colorName, selectedColorData.image, selectedColorData.galleries);
                                            }
                                        }}
                                        variants={color.map((c, index) => ({
                                            id: `color-${index}`,
                                            value: c.name,
                                            label: c.name,
                                            url: c.image,
                                            disabled: !isColorAvailable(c.name)
                                        }))}
                                        className="grid-cols-4 sm:grid-cols-5"
                                    />
                                </div>
                            )}

                            {/* Quantity */}
                            <div className="quantity-group">
                                <label className="variant-label">{t('quantity')}</label>
                                <QuantityInputBasic
                                    quantity={quantity}
                                    onChange={setQuantity}
                                    max={product.max_cart_limit}
                                    min={1}
                                    className="w-fit"
                                />
                                {product.max_cart_limit > 0 && (
                                    <span className="quantity-info">{t('maximum')}: {product.max_cart_limit} {t('units')}</span>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="action-buttons">
                            <button className="btn-add-cart" onClick={handleAddToCart}>
                                <ShoppingCart className="h-5 w-5" />
                                {t('addToCart')}
                            </button>
                            <button
                                className="btn-wishlist"
                                onClick={() => {
                                    if (!userData) {
                                        toast.info('Inicia sesión para agregar a favoritos');
                                        window.location.href = '/login';
                                    } else {
                                        addToWishlist(product.id, userData.user_id);
                                    }
                                }}
                            >
                                <Heart className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Details Section */}
                <div className="details-section">
                    {/* Specifications */}
                    <div className="detail-card">
                        <div className="detail-card-header">
                            <h3>{t('specifications')}</h3>
                        </div>
                        <div className="detail-card-body">
                            {specifications.length > 0 || product?.category ? (
                                <table className="specs-table">
                                    <tbody>
                                        {specifications.map((spec, index) => (
                                            <tr key={index}>
                                                <th>{spec.title}</th>
                                                <td>{spec.content}</td>
                                            </tr>
                                        ))}
                                        <tr>
                                            <th>{t('availability')}</th>
                                            <td>{product.stock_qty > 0 ? t('inStock') : t('outOfStock')}</td>
                                        </tr>
                                        {product?.category?.title && (
                                            <tr>
                                                <th>{t('category')}</th>
                                                <td>{product.category.title}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="text-gray-500 text-center py-4">{t('noSpecifications')}</p>
                            )}
                        </div>
                    </div>

                    {/* Description */}
                    {product?.description && (
                        <div className="detail-card">
                            <div className="detail-card-header">
                                <h3>{t('description')}</h3>
                            </div>
                            <div className="detail-card-body">
                                <div className="description-content">
                                    {product.description
                                        .split('\n')
                                        .filter(p => p.trim() !== '')
                                        .map((paragraph, i) => (
                                            <p key={i}>{paragraph.trim()}</p>
                                        ))
                                    }
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Share */}
                    <div className="detail-card">
                        <div className="share-section">
                            <h4 className="share-title">{t('shareProduct') || 'Comparte este producto'}</h4>
                            <div className="share-buttons">
                                <a
                                    href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="share-btn facebook"
                                >
                                    <i className="fab fa-facebook-f"></i>
                                    Facebook
                                </a>
                                <a
                                    href={`https://twitter.com/intent/tweet?url=${window.location.href}&text=${product?.title}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="share-btn twitter"
                                >
                                    <i className="fab fa-twitter"></i>
                                    Twitter
                                </a>
                                <a
                                    href={`https://wa.me/?text=${product?.title} - ${window.location.href}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="share-btn whatsapp"
                                >
                                    <i className="fab fa-whatsapp"></i>
                                    WhatsApp
                                </a>
                                <button
                                    className="share-btn copy-link"
                                    onClick={() => {
                                        navigator.clipboard.writeText(window.location.href);
                                        toast.success('¡Enlace copiado!');
                                    }}
                                >
                                    <i className="fas fa-link"></i>
                                    Copiar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Related Products */}
                {translatedRelatedProducts.length > 0 && (
                    <div className="related-section">
                        <div className="related-header">
                            <h2 className="related-title">{t('relatedProducts') || 'Productos Relacionados'}</h2>
                        </div>
                        <div className="related-grid">
                            {translatedRelatedProducts.slice(0, 8).map((p, index) => (
                                <Link key={index} to={`/detail/${p.slug}`} className="related-card">
                                    <div className="related-card-image">
                                        <img src={p.image} alt={p.title} />
                                    </div>
                                    <div className="related-card-info">
                                        <h4 className="related-card-title">{p.title}</h4>
                                        <div>
                                            {p.old_price > 0 && (
                                                <span className="related-card-old-price">
                                                    <PriceFormat_Basic value={p.old_price} />
                                                </span>
                                            )}
                                            <span className="related-card-price">
                                                <PriceFormat_Basic value={p.price} />
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile Fixed Bottom Action Bar */}
            <div className="mobile-action-bar">
                <div className="mobile-price">
                    <span className="mobile-price-current">
                        <PriceFormat_Basic value={currentPrice || product?.price || 0} />
                    </span>
                    {product?.old_price > 0 && product.old_price > (currentPrice || product?.price) && (
                        <span className="mobile-price-old">
                            <PriceFormat_Basic value={product.old_price} />
                        </span>
                    )}
                </div>
                <button className="btn-add-cart" onClick={handleAddToCart}>
                    <ShoppingCart className="h-4 w-4" />
                    {t('addToCart')}
                </button>
                <button
                    className="btn-wishlist"
                    onClick={() => {
                        if (!userData) {
                            toast.info('Inicia sesión para agregar a favoritos');
                            window.location.href = '/login';
                        } else {
                            addToWishlist(product.id, userData.user_id);
                        }
                    }}
                >
                    <Heart className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}

export default ProductDetail;
